import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { formatLocalDateYMD, parseDateOnlyToLocal } from '@shared/lib/formatLocalDate';
import { useCallback, useEffect, useState } from 'react';

/** Estado local de filtros para almacenamiento elegible */
export interface EligibleStorageFiltersState {
  storageDateRange: { from: Date | undefined; to: Date | undefined };
  quantityMin: string;
  quantityMax: string;
  preinvoiceFilter: '' | 'is_null' | 'is_not_null' | 'equals';
  preinvoiceId: string;
  invoiceFilter: '' | 'is_null' | 'is_not_null' | 'equals';
  invoiceId: string;
  period: string;
}

const defaultState: EligibleStorageFiltersState = {
  storageDateRange: { from: undefined, to: undefined },
  quantityMin: '',
  quantityMax: '',
  preinvoiceFilter: '',
  preinvoiceId: '',
  invoiceFilter: '',
  invoiceId: '',
  period: '',
};

function cloneDefaultState(): EligibleStorageFiltersState {
  return {
    ...defaultState,
    storageDateRange: { ...defaultState.storageDateRange },
  };
}

function parseConditions(conditions: FilterConditionOutput[]): EligibleStorageFiltersState {
  const state = cloneDefaultState();

  for (const c of conditions) {
    if (c.column === 'storage_date') {
      if (c.action === 'greater_equal' && c.value) {
        state.storageDateRange.from = parseDateOnlyToLocal(c.value);
      } else if (c.action === 'less_equal' && c.value) {
        state.storageDateRange.to = parseDateOnlyToLocal(c.value);
      }
    } else if (c.column === 'quantity') {
      if (c.action === 'greater_equal' || c.action === 'greater_than') {
        state.quantityMin = c.value != null ? String(c.value) : '';
      } else if (c.action === 'less_equal' || c.action === 'less_than') {
        state.quantityMax = c.value != null ? String(c.value) : '';
      }
    } else if (c.column === 'preinvoice_id') {
      if (c.action === 'is_null') state.preinvoiceFilter = 'is_null';
      else if (c.action === 'is_not_null') state.preinvoiceFilter = 'is_not_null';
      else if (c.action === 'equals' && c.value != null) {
        state.preinvoiceFilter = 'equals';
        state.preinvoiceId = String(c.value);
      }
    } else if (c.column === 'invoice_id') {
      if (c.action === 'is_null') state.invoiceFilter = 'is_null';
      else if (c.action === 'is_not_null') state.invoiceFilter = 'is_not_null';
      else if (c.action === 'equals' && c.value != null) {
        state.invoiceFilter = 'equals';
        state.invoiceId = String(c.value);
      }
    } else if (c.column === 'period' && c.action === 'equals' && c.value) {
      state.period = String(c.value);
    }
  }

  return state;
}

function convertToConditions(state: EligibleStorageFiltersState): FilterConditionOutput[] {
  const conditions: FilterConditionOutput[] = [];

  if (state.storageDateRange.from) {
    conditions.push({
      column: 'storage_date',
      action: 'greater_equal',
      value: formatLocalDateYMD(state.storageDateRange.from),
    });
  }
  if (state.storageDateRange.to) {
    conditions.push({
      column: 'storage_date',
      action: 'less_equal',
      value: formatLocalDateYMD(state.storageDateRange.to),
    });
  }
  if (state.quantityMin.trim()) {
    const num = parseFloat(state.quantityMin);
    if (!Number.isNaN(num)) {
      conditions.push({ column: 'quantity', action: 'greater_equal', value: num });
    }
  }
  if (state.quantityMax.trim()) {
    const num = parseFloat(state.quantityMax);
    if (!Number.isNaN(num)) {
      conditions.push({ column: 'quantity', action: 'less_equal', value: num });
    }
  }
  if (state.preinvoiceFilter === 'is_null') {
    conditions.push({ column: 'preinvoice_id', action: 'is_null', value: null });
  } else if (state.preinvoiceFilter === 'is_not_null') {
    conditions.push({ column: 'preinvoice_id', action: 'is_not_null', value: null });
  } else if (state.preinvoiceFilter === 'equals' && state.preinvoiceId.trim()) {
    const num = parseFloat(state.preinvoiceId);
    if (!Number.isNaN(num)) {
      conditions.push({ column: 'preinvoice_id', action: 'equals', value: num });
    }
  }
  if (state.invoiceFilter === 'is_null') {
    conditions.push({ column: 'invoice_id', action: 'is_null', value: null });
  } else if (state.invoiceFilter === 'is_not_null') {
    conditions.push({ column: 'invoice_id', action: 'is_not_null', value: null });
  } else if (state.invoiceFilter === 'equals' && state.invoiceId.trim()) {
    const num = parseFloat(state.invoiceId);
    if (!Number.isNaN(num)) {
      conditions.push({ column: 'invoice_id', action: 'equals', value: num });
    }
  }
  if (state.period) {
    conditions.push({ column: 'period', action: 'equals', value: state.period });
  }

  return conditions;
}

export interface UseEligibleStorageFiltersParams {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}

export const useEligibleStorageFilters = ({ initialConditions = [], onChange }: UseEligibleStorageFiltersParams) => {
  const [filters, setFilters] = useState<EligibleStorageFiltersState>(() => parseConditions(initialConditions));

  useEffect(() => {
    setFilters(parseConditions(initialConditions));
  }, [initialConditions]);

  const notifyChange = useCallback(
    (next: EligibleStorageFiltersState) => {
      onChange?.(convertToConditions(next));
    },
    [onChange],
  );

  const handleStorageDateRangeChange = useCallback(
    (range: { from: Date | undefined; to: Date | undefined }) => {
      setFilters((prev) => {
        const next = { ...prev, storageDateRange: range };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleQuantityMinChange = useCallback(
    (value: string) => {
      setFilters((prev) => {
        const next = { ...prev, quantityMin: value };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleQuantityMaxChange = useCallback(
    (value: string) => {
      setFilters((prev) => {
        const next = { ...prev, quantityMax: value };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handlePreinvoiceFilterChange = useCallback(
    (value: string) => {
      const filter = (value === '' ? '' : value) as EligibleStorageFiltersState['preinvoiceFilter'];
      setFilters((prev) => {
        const next = {
          ...prev,
          preinvoiceFilter: filter,
          preinvoiceId: filter === 'equals' ? prev.preinvoiceId : '',
        };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handlePreinvoiceIdChange = useCallback(
    (value: string) => {
      setFilters((prev) => {
        const next = { ...prev, preinvoiceId: value };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleInvoiceFilterChange = useCallback(
    (value: string) => {
      const filter = (value === '' ? '' : value) as EligibleStorageFiltersState['invoiceFilter'];
      setFilters((prev) => {
        const next = {
          ...prev,
          invoiceFilter: filter,
          invoiceId: filter === 'equals' ? prev.invoiceId : '',
        };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleInvoiceIdChange = useCallback(
    (value: string) => {
      setFilters((prev) => {
        const next = { ...prev, invoiceId: value };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handlePeriodChange = useCallback(
    (value: string) => {
      setFilters((prev) => {
        const next = { ...prev, period: value };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const clearAllFilters = useCallback(() => {
    const empty = cloneDefaultState();
    setFilters(empty);
    notifyChange(empty);
  }, [notifyChange]);

  return {
    filters,
    handleStorageDateRangeChange,
    handleQuantityMinChange,
    handleQuantityMaxChange,
    handlePreinvoiceFilterChange,
    handlePreinvoiceIdChange,
    handleInvoiceFilterChange,
    handleInvoiceIdChange,
    handlePeriodChange,
    clearAllFilters,
  };
};
